import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { ChevronRight, MoreHorizontal } from 'lucide-react'
import { Screen } from '../components/Screen'
import { Field, useLeaveGuard } from '../components/Form'
import { EmptyState, ErrorState, SaveFailedAlert, Sheet, Skeleton } from '../components/States'
import { useEnv } from '../context'
import {
  EVENT_KINDS,
  EVENT_KIND_LABEL,
  EVENT_KIND_PLURAL,
  eventPhase,
  formatEventWhen,
  formatFee,
  isOnDay,
  participantSummary,
  pluralParticipants,
  quickStatuses,
  splitEvents,
} from '../data/events'
import {
  useEvent,
  useEventParticipants,
  useEvents,
  useInvalidateEvent,
  useParticipantMarks,
  useSingleFlight,
  type ParticipantRowState,
} from '../data/hooks'
import { isoDate, parseIsoDate } from '../data/schedule'
import { SliceApiError, type ClubEvent, type EventInput, type EventKind, type EventParticipant, type ParticipantPatch } from '../data/types'
import { useOnline } from '../platform/network'
import { useBackTo } from './AthleteScreen'
import { ParticipantSheet, participantCaption } from './EventSheets'

/** Итог добавления участников: экран добавления кладёт его, карточка события показывает плашку. */
export interface EventAddedNotice {
  added: { id: string; name: string }[]
  already: number
  failed: number
}

export const eventAddedKey = (eventId: string) => ['redesign', 'event-added', eventId] as const

/** Строка события в списках: название, тип, дата, место, участники. */
export function EventRow({ event: e, onOpen }: { event: ClubEvent; onOpen: () => void }) {
  const { now } = useEnv()
  const today = isoDate(now())
  const ongoing = isOnDay(e, today)
  return (
    <button type="button" className="pc-list-row" onClick={onOpen}>
      <span className="pc-list-row__main">
        <span className="pc-list-row__title" style={{ display: 'block' }}>
          {e.name}
        </span>
        <span className="pc-list-row__meta" style={{ display: 'block' }}>
          {EVENT_KIND_LABEL[e.kind]} / {formatEventWhen(e, now().getFullYear())}
        </span>
        {(e.location || e.participantCount !== undefined) && (
          <span className="pc-list-row__meta" style={{ display: 'block' }}>
            {[e.location, e.participantCount !== undefined ? pluralParticipants(e.participantCount) : null]
              .filter(Boolean)
              .join(' / ')}
          </span>
        )}
        {ongoing && (
          <span className="pc-badge" style={{ marginTop: 6 }}>
            {e.start.date === today ? 'Сегодня' : 'Идёт сейчас'}
          </span>
        )}
      </span>
      <ChevronRight size={18} aria-hidden="true" className="pc-muted" />
    </button>
  )
}

/**
 * «Соревнования и сборы» (events.md): ближайшие и прошедшие, фильтр по типу.
 * Вид и фильтр - в адресе, возврат из карточки события их сохраняет.
 */
export function EventsScreen() {
  const { now } = useEnv()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const back = useBackTo('/schedule')
  const events = useEvents()
  const today = isoDate(now())
  const view: 'upcoming' | 'past' = params.get('v') === 'past' ? 'past' : 'upcoming'
  const kind = EVENT_KINDS.find((k) => k === params.get('t')) ?? null

  const set = (next: { v?: string | null; t?: string | null }) => {
    const p = new URLSearchParams(params)
    for (const [k, v] of Object.entries(next)) {
      if (v) p.set(k, v)
      else p.delete(k)
    }
    setParams(p, { replace: true })
  }

  const split = useMemo(() => splitEvents(events.data ?? [], today), [events.data, today])
  const all = events.data ?? []
  const kinds = EVENT_KINDS.filter((k) => all.some((e) => e.kind === k))
  const list = (view === 'past' ? split.past : split.upcoming).filter((e) => !kind || e.kind === kind)

  let body
  if (events.isPending) body = <Skeleton rows={3} label="Загружаю события" />
  else if (events.isError) body = <ErrorState title="Не удалось загрузить события" onRetry={() => void events.refetch()} />
  else if (all.length === 0) {
    body = (
      <EmptyState
        title="Событий пока нет"
        text="Соревнования, сборы и аттестации - с датой, местом и списком участников. Ребят можно добавить целой группой."
      />
    )
  } else if (list.length === 0) {
    body = (
      <EmptyState
        title={kind ? 'Нет событий этого типа' : view === 'past' ? 'Прошедших событий нет' : 'Ближайших событий нет'}
        text={kind ? 'Попробуйте другой тип или «Все».' : undefined}
      />
    )
  } else {
    body = (
      <ul className="pc-list pc-card" style={{ padding: '0 12px', marginTop: 12 }}>
        {list.map((e) => (
          <li key={e.id}>
            <EventRow event={e} onOpen={() => navigate(`/schedule/events/${e.id}`)} />
          </li>
        ))}
      </ul>
    )
  }

  return (
    <Screen
      title="Соревнования и сборы"
      back={back}
      actionBar={
        <button type="button" className="pc-btn pc-btn--primary pc-btn--block" onClick={() => navigate('/schedule/events/new')}>
          Новое событие
        </button>
      }
    >
      <div className="pc-segments" role="group" aria-label="Какие события" style={{ marginTop: 16 }}>
        <button type="button" aria-pressed={view === 'upcoming'} onClick={() => set({ v: null })}>
          Ближайшие {events.data ? split.upcoming.length : ''}
        </button>
        <button type="button" aria-pressed={view === 'past'} onClick={() => set({ v: 'past' })}>
          Прошедшие {events.data ? split.past.length : ''}
        </button>
      </div>
      {kinds.length > 1 && (
        <div className="pc-chips pc-chips--tall" role="group" aria-label="Тип события">
          <button type="button" aria-pressed={!kind} onClick={() => set({ t: null })}>
            Все
          </button>
          {kinds.map((k) => (
            <button key={k} type="button" aria-pressed={kind === k} onClick={() => set({ t: k })}>
              {EVENT_KIND_PLURAL[k]}
            </button>
          ))}
        </div>
      )}
      {body}
    </Screen>
  )
}

/**
 * Карточка события: когда, где, взнос, участники со статусом по касанию.
 * Вкладка «Взнос» - только если взнос задан. Удаление - через меню и
 * подтверждение.
 */
export function EventScreen() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const { api, now, toast } = useEnv()
  const client = useQueryClient()
  const back = useBackTo('/schedule/events')
  const event = useEvent(id)
  const parts = useEventParticipants(id)
  const marks = useParticipantMarks(id)
  const invalidate = useInvalidateEvent()
  const flight = useSingleFlight()
  const online = useOnline()
  const [menu, setMenu] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleteFailed, setDeleteFailed] = useState(false)
  const [sheetFor, setSheetFor] = useState<string | null>(null)
  const [notice, setNotice] = useState<EventAddedNotice | null>(
    () => client.getQueryData<EventAddedNotice>(eventAddedKey(id)) ?? null,
  )
  const [undo, setUndo] = useState(false)
  const [undoState, setUndoState] = useState<{ busy: boolean; failed: number }>({ busy: false, failed: 0 })

  // Плашка «Добавлено» показывается один раз: при следующем открытии её нет.
  useEffect(() => {
    client.removeQueries({ queryKey: eventAddedKey(id), exact: true })
  }, [client, id])

  // «Взнос»: сначала те, у кого нет отметки (кто ещё не сдал). Порядок
  // запоминается при открытии вкладки, иначе строка уезжала бы из-под пальца
  // после каждой отметки.
  const wantsFee = params.get('tab') === 'fee'
  const [feeOrder, setFeeOrder] = useState<string[] | null>(null)
  useEffect(() => {
    if (!wantsFee) return setFeeOrder(null)
    if (!parts.data) return
    setFeeOrder(
      (prev) =>
        prev ??
        [...parts.data]
          .sort((a, b) => Number(a.payment !== 'not_paid') - Number(b.payment !== 'not_paid'))
          .map((p) => p.athleteId),
    )
  }, [wantsFee, parts.data])

  const today = isoDate(now())

  if (event.isPending) {
    return (
      <Screen title={' '} back={back}>
        <Skeleton rows={3} label="Загружаю событие" />
      </Screen>
    )
  }
  if (event.isError) {
    const notFound = (event.error as SliceApiError)?.kind === 'not_found'
    return (
      <Screen title="Событие" back={back}>
        {notFound ? (
          <EmptyState title="Событие не найдено" text="Возможно, его удалили или ссылка устарела." />
        ) : (
          <ErrorState title="Не удалось открыть событие" onRetry={() => void event.refetch()} />
        )}
      </Screen>
    )
  }

  const e = event.data
  const phase = eventPhase(e, today)
  const hasFee = !!e.fee
  const tab: 'status' | 'fee' = hasFee && wantsFee ? 'fee' : 'status'
  const list = parts.data ?? []
  const current = sheetFor ? list.find((p) => p.athleteId === sheetFor) : undefined

  const remove = async (athleteId: string): Promise<boolean> => {
    try {
      const res = await flight.run(() => api.removeEventParticipant(e.id, athleteId))
      if (res === null) return false
      await invalidate(e.id)
      return true
    } catch {
      return false
    }
  }

  const deleteEvent = async () => {
    setDeleteFailed(false)
    try {
      const res = await flight.run(() => api.deleteEvent(e.id))
      if (res === null) return
      await invalidate()
      client.removeQueries({ queryKey: ['redesign', 'event', e.id] })
      toast(`Событие удалено: ${e.name}`)
      navigate('/schedule/events', { replace: true })
    } catch {
      setDeleteFailed(true)
    }
  }

  // Плашка считает только тех, кто всё ещё в событии: убрали вручную или
  // сменился состав - «Отменить» не трогает чужое и не висит без дела.
  const stillAdded = notice ? notice.added.filter((a) => !parts.data || list.some((p) => p.athleteId === a.id)) : []

  const undoAdded = async () => {
    if (!notice || undoState.busy || stillAdded.length === 0) return
    setUndoState({ busy: true, failed: 0 })
    let failed = 0
    const left: { id: string; name: string }[] = []
    for (const a of stillAdded) {
      try {
        await api.removeEventParticipant(e.id, a.id)
      } catch {
        failed += 1
        left.push(a)
      }
    }
    await invalidate(e.id)
    setUndoState({ busy: false, failed })
    if (failed === 0) {
      toast(`Добавление отменено: ${stillAdded.length}`)
      setNotice(null)
      setUndo(false)
    } else {
      setNotice({ ...notice, added: left })
    }
  }

  const paid = list.filter((p) => p.payment === 'paid').length
  const exempt = list.filter((p) => p.payment === 'exempt').length
  const rank = (id: string) => {
    const i = feeOrder?.indexOf(id) ?? -1
    return i < 0 ? Number.MAX_SAFE_INTEGER : i
  }
  const shown = tab === 'fee' && feeOrder ? [...list].sort((a, b) => rank(a.athleteId) - rank(b.athleteId)) : list

  return (
    <Screen
      title={e.name}
      subtitle={`${EVENT_KIND_LABEL[e.kind]} / ${formatEventWhen(e, now().getFullYear())}`}
      back={back}
      hubContext={{ label: `${e.name}, ${EVENT_KIND_LABEL[e.kind].toLowerCase()} ${formatEventWhen(e, now().getFullYear())}` }}
      headerAction={
        <button type="button" className="pc-icon-btn" aria-label="Действия события" onClick={() => setMenu(true)}>
          <MoreHorizontal size={20} aria-hidden="true" />
        </button>
      }
      actionBar={
        <button type="button" className="pc-btn pc-btn--primary pc-btn--block" onClick={() => navigate(`/schedule/events/${e.id}/add`)}>
          Добавить участников
        </button>
      }
    >
      <dl className="pc-card pc-ev-info" aria-label="О событии">
        <div>
          <dt>Когда</dt>
          <dd>{whenFull(e)}</dd>
        </div>
        <div>
          <dt>Где</dt>
          <dd>{e.location || 'Место не указано'}</dd>
        </div>
        {hasFee && (
          <div>
            <dt>Взнос</dt>
            <dd>{formatFee(e.fee!)}</dd>
          </div>
        )}
        {e.description && (
          <div>
            <dt>Описание</dt>
            <dd>{e.description}</dd>
          </div>
        )}
      </dl>

      {notice && stillAdded.length > 0 && (
        <div className="pc-note" role="status" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ flex: 1 }}>
            Добавлено: {stillAdded.length}
            {notice.already > 0 ? `. Уже были в списке: ${notice.already}` : ''}
            {notice.failed > 0 ? `. Не добавлено: ${notice.failed}` : ''}
          </span>
          <button type="button" className="pc-btn" onClick={() => setUndo(true)}>
            Отменить
          </button>
        </div>
      )}

      <section className="pc-section" aria-labelledby="pc-ev-people">
        <h2 id="pc-ev-people" className="pc-section-title">
          Участники{parts.data ? ` / ${list.length}` : ''}
        </h2>
        {hasFee && (
          <div className="pc-segments" role="tablist" aria-label="Участники">
            <button type="button" role="tab" aria-selected={tab === 'status'} onClick={() => setParams({}, { replace: true })}>
              Участие
            </button>
            <button type="button" role="tab" aria-selected={tab === 'fee'} onClick={() => setParams({ tab: 'fee' }, { replace: true })}>
              Взнос
            </button>
          </div>
        )}
        {parts.isPending ? (
          <Skeleton rows={3} label="Загружаю участников" />
        ) : parts.isError ? (
          <ErrorState title="Не удалось загрузить участников" onRetry={() => void parts.refetch()} />
        ) : list.length === 0 ? (
          <EmptyState
            title="Участников пока нет"
            text="Добавьте ребят целой группой или по одному: кто уже в списке, второй раз не добавится."
          />
        ) : (
          <>
            <div className="pc-roll-summary" style={{ marginTop: 12 }}>
              <strong>
                {tab === 'fee'
                  ? `Оплата отмечена: ${paid} из ${list.length}`
                  : phase === 'before'
                    ? `Идут: ${list.filter((p) => p.status === 'confirmed').length} из ${list.length}`
                    : `Отмечено: ${list.filter((p) => p.status === 'participated' || p.status === 'no_show').length} из ${list.length}`}
              </strong>
            </div>
            <p className="pc-muted" style={{ margin: '4px 0 0', fontSize: 14 }}>
              {tab === 'fee'
                ? `${exempt ? `Без оплаты: ${exempt}. ` : ''}Нет отметки - не долг: суммы приложение не считает.`
                : `${capitalize(participantSummary(list, phase))}. Отметка сохраняется сразу.`}
            </p>
            <div aria-live="polite" className="pc-visually-hidden">
              {marks.saving > 0 ? `Сохраняю: ${marks.saving}` : ''}
            </div>
            {marks.failed > 0 && (
              <div className="pc-alert" role="alert">
                <span style={{ flex: 1 }}>
                  {online ? 'Не сохранено' : 'Нет сети - не сохранено'}: {marks.failed}
                </span>
                <button type="button" className="pc-btn" onClick={marks.retryFailed}>
                  Повторить
                </button>
              </div>
            )}
            <ul className="pc-list" style={{ marginTop: 8 }}>
              {shown.map((p) => (
                <li key={p.athleteId}>
                  <ParticipantRow
                    participant={p}
                    mode={tab}
                    phase={phase}
                    state={marks.states[p.athleteId]}
                    onSet={(patch) => void marks.set(p.athleteId, patch)}
                    onMore={() => setSheetFor(p.athleteId)}
                  />
                </li>
              ))}
            </ul>
          </>
        )}
      </section>

      {current && (
        <ParticipantSheet
          title={current.name}
          eventName={e.name}
          hasFee={hasFee}
          participant={current}
          state={marks.states[current.athleteId]}
          onSet={(patch) => void marks.set(current.athleteId, patch)}
          onRemove={() => remove(current.athleteId)}
          link={{ label: 'Открыть карточку спортсмена', onClick: () => navigate(`/athletes/${current.athleteId}`) }}
          onClose={() => setSheetFor(null)}
        />
      )}

      {menu && (
        <Sheet title={e.name} onClose={() => setMenu(false)}>
          <button
            type="button"
            className="pc-btn pc-btn--block"
            onClick={() => {
              setMenu(false)
              navigate(`/schedule/events/${e.id}/edit`)
            }}
          >
            Изменить
          </button>
          <button
            type="button"
            className="pc-btn pc-btn--block pc-btn--danger"
            onClick={() => {
              setMenu(false)
              setConfirmDelete(true)
            }}
          >
            Удалить событие
          </button>
        </Sheet>
      )}

      {confirmDelete && (
        <Sheet title="Удалить событие?" onClose={() => setConfirmDelete(false)}>
          <p className="pc-muted" style={{ margin: '0 0 12px', fontSize: 14 }}>
            «{e.name}» пропадёт из расписания и из карточек спортсменов
            {list.length > 0 ? ` вместе со списком участников (${pluralParticipants(list.length)})` : ''}. Вернуть из
            приложения нельзя.
          </p>
          {deleteFailed && <SaveFailedAlert>Событие на месте, попробуйте ещё раз.</SaveFailedAlert>}
          <button
            type="button"
            className="pc-btn pc-btn--block pc-btn--danger"
            style={{ marginTop: 12 }}
            disabled={flight.busy}
            onClick={() => void deleteEvent()}
          >
            {flight.busy ? 'Удаляю…' : 'Удалить событие'}
          </button>
          <button type="button" className="pc-btn pc-btn--block" onClick={() => setConfirmDelete(false)}>
            Отмена
          </button>
        </Sheet>
      )}

      {undo && notice && stillAdded.length > 0 && (
        <Sheet title={`Убрать добавленных: ${stillAdded.length}?`} onClose={() => setUndo(false)}>
          <p className="pc-muted" style={{ margin: '0 0 8px', fontSize: 14 }}>
            Из «{e.name}» уберутся только эти спортсмены - те, кого добавили сейчас:
          </p>
          <ul className="pc-list" style={{ marginBottom: 12 }}>
            {stillAdded.map((a) => (
              <li key={a.id} className="pc-kv">
                {a.name}
              </li>
            ))}
          </ul>
          {undoState.failed > 0 && (
            <div className="pc-alert" role="alert" style={{ marginBottom: 12 }}>
              {online ? `Не убрано: ${undoState.failed}. Попробуйте ещё раз.` : 'Нет сети - не сохранено.'}
            </div>
          )}
          <button
            type="button"
            className="pc-btn pc-btn--block pc-btn--danger"
            disabled={undoState.busy}
            onClick={() => void undoAdded()}
          >
            {undoState.busy ? 'Убираю…' : `Убрать ${stillAdded.length}`}
          </button>
          <button type="button" className="pc-btn pc-btn--block" onClick={() => setUndo(false)}>
            Оставить
          </button>
        </Sheet>
      )}
    </Screen>
  )
}

function capitalize(s: string): string {
  return s ? s[0].toUpperCase() + s.slice(1) : s
}

/** «Суббота, 18 октября 2026, 10:00» или «1 - 10 июля 2026», время окончания если есть. */
function whenFull(e: ClubEvent): string {
  const d = parseIsoDate(e.start.date)
  const base = formatEventWhen(e, -1, false)
  if (!d) return base
  const sameDay = !e.end || e.end.date === e.start.date
  const times = [e.start.time, sameDay ? e.end?.time : ''].filter(Boolean).join(' - ')
  if (sameDay) return `${base}${times ? `, ${times}` : ', весь день'}`
  const start = e.start.time ? `начало ${e.start.time}` : ''
  const end = e.end?.time ? `окончание ${e.end.time}` : ''
  return [base, start, end].filter(Boolean).join(', ')
}

/** Строка участника: статус по касанию, как отметка на тренировке. */
function ParticipantRow({
  participant: p,
  mode,
  phase,
  state,
  onSet,
  onMore,
}: {
  participant: EventParticipant
  mode: 'status' | 'fee'
  phase: 'before' | 'started'
  state?: ParticipantRowState
  onSet: (patch: ParticipantPatch) => void
  onMore: () => void
}) {
  const online = useOnline()
  const saving = state?.phase === 'saving'
  const pending = state?.patch
  const caption = participantCaption(p, state, online, mode)
  const failed = state?.phase === 'error'

  return (
    <div className="pc-roll-row">
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="pc-roll-row__name">{p.name}</div>
        <div className="pc-roll-row__state" data-tone={caption.tone}>
          {caption.text}
          {failed && (
            <>
              {' - '}
              <button type="button" className="pc-btn pc-btn--link" style={{ fontSize: 13 }} onClick={() => onSet(state.patch)}>
                Повторить
              </button>
            </>
          )}
        </div>
      </div>
      {mode === 'status' ? (
        <div className="pc-mark pc-mark--event" role="group" aria-label={`Участие: ${p.name}`}>
          {quickStatuses(phase).map(([s, label], i) => {
            const shown = pending?.status ?? p.status
            return (
              <button
                key={s}
                type="button"
                data-kind={i === 0 ? 'yes' : 'no'}
                aria-pressed={shown === s}
                data-pending={!!pending?.status && pending.status === s}
                disabled={saving}
                onClick={() => onSet({ status: s })}
              >
                {label}
              </button>
            )
          })}
          <button type="button" className="pc-mark__more" aria-label={`Ещё: ${p.name}`} disabled={saving} onClick={onMore}>
            <MoreHorizontal size={20} aria-hidden="true" />
          </button>
        </div>
      ) : (
        <div className="pc-mark" role="group" aria-label={`Взнос: ${p.name}`}>
          {p.payment === 'paid' ? (
            <button type="button" disabled={saving} aria-label={`Снять отметку оплаты: ${p.name}`} onClick={() => onSet({ payment: 'not_paid' })}>
              Снять отметку
            </button>
          ) : (
            <button
              type="button"
              data-kind="yes"
              aria-pressed={pending?.payment === 'paid'}
              data-pending={pending?.payment === 'paid'}
              disabled={saving}
              aria-label={`Отметить оплату: ${p.name}`}
              onClick={() => onSet({ payment: 'paid' })}
            >
              Отметить оплату
            </button>
          )}
          <button type="button" className="pc-mark__more" aria-label={`Ещё: ${p.name}`} disabled={saving} onClick={onMore}>
            <MoreHorizontal size={20} aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  )
}

const TIME_RE = /^\d{2}:\d{2}$/

/**
 * Новое событие и правка (`/schedule/events/new`, `/schedule/events/:id/edit`).
 * Время необязательно: без него событие - на весь день. Окончание в тот же
 * день без времени не отправляется. Выход без сохранения спрашивает.
 */
export function EventFormScreen() {
  const { id } = useParams()
  const navigate = useNavigate()
  const event = useEvent(id ?? '')
  if (id && event.isPending) {
    return (
      <Screen title="Изменить событие" back={() => navigate(-1)}>
        <Skeleton rows={3} label="Загружаю событие" />
      </Screen>
    )
  }
  if (id && event.isError) {
    return (
      <Screen title="Изменить событие" back={() => navigate(-1)}>
        <ErrorState title="Не удалось открыть событие" onRetry={() => void event.refetch()} />
      </Screen>
    )
  }
  return <EventForm key={id ?? 'new'} initial={id ? event.data : undefined} />
}

function EventForm({ initial }: { initial?: ClubEvent }) {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const { api, toast } = useEnv()
  const invalidate = useInvalidateEvent()
  const flight = useSingleFlight()
  const start0 = initial?.start ?? { date: parseIsoDate(params.get('d')) ? params.get('d')! : '', time: '' }
  const [name, setName] = useState(initial?.name ?? '')
  const [kind, setKind] = useState<EventKind>(initial?.kind ?? 'competition')
  const [startDate, setStartDate] = useState(start0.date)
  const [startTime, setStartTime] = useState(start0.time)
  const [endDate, setEndDate] = useState(initial?.end?.date ?? '')
  const [endTime, setEndTime] = useState(initial?.end?.time ?? '')
  const [location, setLocation] = useState(initial?.location ?? '')
  const [fee, setFee] = useState(initial?.fee ? String(Number(initial.fee)) : '')
  const [description, setDescription] = useState(initial?.description ?? '')
  const [errors, setErrors] = useState<Partial<Record<'name' | 'start' | 'end' | 'fee', string>>>({})
  const [saveError, setSaveError] = useState<string | null>(null)

  const snapshot = JSON.stringify([name, kind, startDate, startTime, endDate, endTime, location, fee, description])
  const [initialSnapshot] = useState(snapshot)
  const dirty = snapshot !== initialSnapshot
  const leave = useLeaveGuard(dirty && !flight.busy)
  const back = () => leave.guard(() => navigate(-1))

  // Сохранено: экран уходит, второе нажатие не создаёт второе событие.
  const saved = useRef(false)
  const save = async () => {
    if (saved.current) return
    const e: typeof errors = {}
    if (!name.trim()) e.name = 'Укажите название'
    if (!parseIsoDate(startDate)) e.start = 'Укажите дату начала'
    if (startTime && !TIME_RE.test(startTime)) e.start = 'Время в формате ЧЧ:ММ'
    const feeNorm = fee.trim().replace(',', '.')
    if (feeNorm && !(Number(feeNorm) >= 0 && /^\d+(\.\d{1,2})?$/.test(feeNorm))) e.fee = 'Сумма числом, например 1500'
    let end: EventInput['end']
    if (endDate) {
      if (!parseIsoDate(endDate)) e.end = 'Дата в формате ГГГГ-ММ-ДД'
      else if (endDate < startDate) e.end = 'Окончание раньше начала'
      else if (endDate === startDate && endTime && startTime && endTime <= startTime) e.end = 'Окончание раньше начала'
      // В тот же день без времени окончание ничего не добавляет
      else if (endDate > startDate || endTime) end = { date: endDate, time: endTime }
    } else if (endTime) {
      e.end = 'Укажите дату окончания'
    }
    setErrors(e)
    if (Object.keys(e).length > 0) return

    const input: EventInput = {
      name,
      kind,
      start: { date: startDate, time: startTime },
      end,
      location,
      description,
      fee: feeNorm,
    }
    setSaveError(null)
    try {
      const result = await flight.run(() => (initial ? api.updateEvent(initial.id, input) : api.createEvent(input)))
      if (!result) return
      saved.current = true
      await invalidate(result.id)
      toast(initial ? 'Событие сохранено' : 'Событие создано')
      if (initial) navigate(-1)
      else navigate(`/schedule/events/${result.id}`, { replace: true })
    } catch (err) {
      setSaveError(err instanceof SliceApiError && err.kind === 'limit' ? 'limit' : 'failed')
    }
  }

  return (
    <Screen
      title={initial ? 'Изменить событие' : 'Новое событие'}
      back={back}
      actionBar={
        <button type="button" className="pc-btn pc-btn--primary pc-btn--block" disabled={flight.busy} onClick={() => void save()}>
          {flight.busy ? 'Сохраняю…' : initial ? 'Сохранить' : 'Создать событие'}
        </button>
      }
    >
      <form
        className="pc-form"
        noValidate
        onSubmit={(ev) => {
          ev.preventDefault()
          void save()
        }}
      >
        {saveError === 'limit' ? (
          <div className="pc-alert" role="alert">
            Подписка клуба закончилась: изменения сейчас не сохраняются. Подробности - в «Клуб» - «Подписка».
          </div>
        ) : saveError ? (
          <SaveFailedAlert>Данные остались на экране, попробуйте ещё раз.</SaveFailedAlert>
        ) : null}
        <Field label="Название" error={errors.name}>
          {(p) => (
            <input
              {...p}
              className="pc-input"
              value={name}
              onChange={(ev) => setName(ev.target.value)}
              placeholder="Например: Кубок осени"
              autoComplete="off"
            />
          )}
        </Field>
        <div className="pc-field">
          <span className="pc-field__label" id="pc-ev-kind">
            Тип
          </span>
          <div className="pc-chips pc-chips--tall" role="group" aria-labelledby="pc-ev-kind" style={{ flexWrap: 'wrap', marginTop: 0 }}>
            {EVENT_KINDS.map((k) => (
              <button key={k} type="button" aria-pressed={kind === k} onClick={() => setKind(k)}>
                {EVENT_KIND_LABEL[k]}
              </button>
            ))}
          </div>
        </div>
        <div className="pc-ev-dt">
          <Field label="Дата начала" error={errors.start}>
            {(p) => <input {...p} className="pc-input" type="date" value={startDate} onChange={(ev) => setStartDate(ev.target.value)} />}
          </Field>
          <Field label="Время">
            {(p) => <input {...p} className="pc-input" type="time" value={startTime} onChange={(ev) => setStartTime(ev.target.value)} />}
          </Field>
        </div>
        <div className="pc-ev-dt">
          <Field label="Окончание" error={errors.end}>
            {(p) => (
              <input
                {...p}
                className="pc-input"
                type="date"
                value={endDate}
                min={startDate || undefined}
                onChange={(ev) => setEndDate(ev.target.value)}
              />
            )}
          </Field>
          <Field label="Время">
            {(p) => <input {...p} className="pc-input" type="time" value={endTime} onChange={(ev) => setEndTime(ev.target.value)} />}
          </Field>
          <p className="pc-field__hint" style={{ gridColumn: '1 / -1', margin: '-2px 0 0' }}>
            Окончание - для сборов и событий на несколько дней. Без времени событие идёт весь день.
          </p>
        </div>
        <Field label="Место">
          {(p) => (
            <input {...p} className="pc-input" value={location} onChange={(ev) => setLocation(ev.target.value)} autoComplete="off" />
          )}
        </Field>
        <Field label="Взнос, ₽" error={errors.fee} hint="Если указать, у участников появится отметка взноса">
          {(p) => (
            <input
              {...p}
              className="pc-input"
              inputMode="decimal"
              value={fee}
              onChange={(ev) => setFee(ev.target.value)}
              placeholder="Без взноса"
              autoComplete="off"
            />
          )}
        </Field>
        <Field label="Описание" hint="Что взять, во сколько сбор">
          {(p) => <textarea {...p} className="pc-input" value={description} onChange={(ev) => setDescription(ev.target.value)} />}
        </Field>
        {initial && (
          <p className="pc-muted" style={{ margin: 0, fontSize: 14 }}>
            Очищенные поля сотрутся.
          </p>
        )}
      </form>
      {leave.dialog}
    </Screen>
  )
}
